import { ArrowBackRounded } from "@mui/icons-material";
import { Box, Grid, Typography, useTheme } from "@mui/material";
import FocusedDrawer from "components/Conversations/Views/Drawer/FocusedDrawer";
import FocusedConversation from "components/Conversations/Views/Main/FocusedConversation/FocusedConversation";
import SideControlsPage from "Layouts/SideControls/SideControlsPage";
import React from "react";
import {
    Route,
    Routes,
    useMatch,
    useNavigate,
    useParams,
} from "react-router-dom";
import FindingConversationsList from "./FindingConversationsList";
import { useFindingConversationIds } from "./useFindingConversationIds";

/** The conversations KBo Flow researched for a single report finding. */
const KboFindingConversations: React.FC = () => {
    const { chatId, findingId } = useParams();
    if (!chatId || !findingId) return null;

    // Keyed so switching findings (e.g. from the KBo drawer) starts fresh.
    return (
        <FindingConversationsPage
            key={findingId}
            chatId={chatId}
            findingId={findingId}
        />
    );
};

type FindingConversationsPageProps = {
    chatId: string;
    findingId: string;
};

const FindingConversationsPage: React.FC<FindingConversationsPageProps> = ({
    chatId,
    findingId,
}) => {
    const navigate = useNavigate();
    const conversationIds = useFindingConversationIds(chatId, findingId);
    const isViewingConversation = !useMatch(
        `/app/kbo/chats/${chatId}/conversations/${findingId}`,
    );

    return (
        <>
            <Routes>
                <Route path=":id" element={<FocusedFindingConversation />} />
                <Route index element={null} />
            </Routes>
            {/* Stays mounted behind an open conversation so its page and sort survive going back. */}
            <Box hidden={isViewingConversation}>
                <Grid
                    container
                    direction="column"
                    wrap="nowrap"
                    sx={{
                        height: "calc(100% - 72px)",
                        width: "100%",
                        overflowY: "auto",
                        padding: 3,
                        gap: 2,
                    }}
                >
                    <Grid item>
                        <Box
                            component="button"
                            type="button"
                            onClick={() => navigate(`/app/kbo/chats/${chatId}`)}
                            sx={{
                                alignItems: "center",
                                bgcolor: "transparent",
                                border: 0,
                                color: "text.secondary",
                                cursor: "pointer",
                                display: "flex",
                                gap: 1,
                                p: 0,
                            }}
                        >
                            <ArrowBackRounded />
                            <Typography sx={{ fontWeight: 600 }}>
                                Back to KBo
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs>
                        <FindingConversationsList
                            conversationIds={conversationIds}
                        />
                    </Grid>
                </Grid>
            </Box>
        </>
    );
};

/**
 * An open conversation, laid out like Conversation Explorer's focused view so it keeps the
 * Details drawer. The drawer's back arrow returns to the finding's list.
 */
const FocusedFindingConversation: React.FC = () => {
    const theme = useTheme();

    return (
        <SideControlsPage
            drawerMinWidth={248}
            drawerMaxWidth={400}
            drawerContent={<FocusedDrawer />}
            mainContent={<FocusedConversation currentRoute="" />}
            drawerLocation="left"
            mainGridSpacing={0}
            mainGridJustify="flex-start"
            mainGridAlignItems="stretch"
            containerOffsetPixels={64}
            drawerVerticalOffsetPixels={0}
            mainSx={{
                paddingTop: theme.spacing(5),
                paddingLeft: theme.spacing(5),
                paddingRight: theme.spacing(5),
            }}
            addMargin="0.5rem"
        />
    );
};

export default KboFindingConversations;
